import React, { useState } from 'react';
import { ALLKEYS, regenDocs } from './docgen';
import { DEFAULT_AGENT } from './types';
import type { Project } from './types';

/** 마스터·LP 스냅샷 — 변경 감지용 */
export function masterHash(p: Project): string {
  return JSON.stringify({
    m: p.master || null,
    ln: p.collect ? p.collect.lps.map((l) => l.name) : [],
    g: p.collect ? p.collect.gp.name : '',
  });
}

interface Snap { m?: unknown; ln?: unknown; g?: unknown; }
interface Chg { label: string; from: string; to: string; }

const F_LABELS: Record<string, string> = { org: '조합명', addr: '조합 소재지', zip: '조합 우편번호', total: '출자총액', unit: '1좌 금액', dur: '존속기간', applyDate: '신청일', regApplyDate: '결성총회일(등록 서류)', uniqueNo: '납세번호' };
const GP_LABELS: Record<string, string> = { name: 'GP 성명', rrn: 'GP 주민번호', addr: 'GP 주소', zip: 'GP 우편번호', edu: 'GP 최종학력', career: 'GP 주요경력', tel: 'GP 전화', email: 'GP 이메일', amt: 'GP 출자금' };
const LP_LABELS: Record<string, string> = { name: '성명', rrn: '주민번호', addr: '주소', amt: '출자금', rel: '관계' };
const EDU_LABELS: Record<string, string> = { name: '교육명', org: '교육기관', period: '교육기간', date: '교육이수일' };
const AGENT_LABELS: Record<string, string> = { name: '대리인 성명', rrn: '대리인 주민번호', tel: '대리인 전화', rel: '대리인 관계' };
const S4_LABELS: Record<string, string> = { owner: '임대인', ownerRrn: '임대인 주민번호', leasePeriod: '임대기간', leaseDate: '임대 시작일', openDate: '개업일', area: '임대면적' };

function asObj(v: unknown): Record<string, unknown> { return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {}; }
function asArr(v: unknown): Record<string, unknown>[] { return Array.isArray(v) ? v as Record<string, unknown>[] : []; }
function str(v: unknown): string { return v === undefined || v === null ? '' : String(v); }
function dval(v: unknown): string { const s = str(v); return s === '' ? '(빈값)' : s; }
function diffObj(labels: Record<string, string>, a: Record<string, unknown>, b: Record<string, unknown>, prefix: string, out: Chg[]): void {
  Object.keys(labels).forEach((k) => { if (str(a[k]) !== str(b[k])) out.push({ label: prefix + labels[k], from: dval(a[k]), to: dval(b[k]) }); });
}
/** 이전 스냅샷(mhash) ↔ 현재값 항목별 차이 */
export function masterChanges(oldJson: string, curJson: string): Chg[] {
  let o: Snap; let c: Snap;
  try { o = JSON.parse(oldJson) as Snap; c = JSON.parse(curJson) as Snap; } catch { return []; }
  const out: Chg[] = [];
  const om = asObj(o.m); const cm = asObj(c.m);
  diffObj(F_LABELS, om, cm, '', out);
  diffObj(GP_LABELS, asObj(om.gp), asObj(cm.gp), '', out);
  diffObj(EDU_LABELS, asObj(om.edu), asObj(cm.edu), '', out);
  diffObj(S4_LABELS, asObj(om.s4), asObj(cm.s4), '', out);
  // 위임장 대리인 — 없으면 지도사 본인으로 보고 비교(예전 조합에 처음 고칠 때 「(빈값) → 임우승」으로 보이지 않게)
  const ag = (s4: unknown): Record<string, unknown> => { const a = asObj(asObj(s4).agent); return Object.keys(a).length ? a : { ...DEFAULT_AGENT }; };
  diffObj(AGENT_LABELS, ag(om.s4), ag(cm.s4), '', out);
  const ol = asArr(om.lps); const cl = asArr(cm.lps);
  for (let i = 0; i < Math.max(ol.length, cl.length); i++) {
    const a = ol[i]; const b = cl[i];
    if (!a && b) { out.push({ label: 'LP' + (i + 1), from: '(없음)', to: str(b.name) || '추가됨' }); continue; }
    if (a && !b) { out.push({ label: 'LP' + (i + 1), from: str(a.name) || '(있음)', to: '(삭제됨)' }); continue; }
    if (a && b) diffObj(LP_LABELS, a, b, 'LP' + (i + 1) + ' ', out);
  }
  if (str(o.g) !== str(c.g)) out.push({ label: 'GP 성명(자료수집)', from: dval(o.g), to: dval(c.g) });
  const oln = Array.isArray(o.ln) ? o.ln as unknown[] : []; const cln = Array.isArray(c.ln) ? c.ln as unknown[] : [];
  for (let i = 0; i < Math.max(oln.length, cln.length); i++) {
    if (str(oln[i]) !== str(cln[i])) out.push({ label: 'LP' + (i + 1) + ' 성명(자료수집)', from: dval(oln[i]), to: dval(cln[i]) });
  }
  return out;
}

interface Props { p: Project; onPatch: (x: Partial<Project>) => void; toast: (m: string) => void; }

export default function RegenBanner({ p, onPatch, toast }: Props) {
  const [busy, setBusy] = useState(false);
  if (!p.master || !p.mhash) return null;
  const cur = masterHash(p);
  if (cur === p.mhash && !busy) return null;

  const regen = async () => {
    setBusy(true);
    try {
      await regenDocs(p, ALLKEYS, onPatch, toast);
      onPatch({ mhash: masterHash(p) });
    } catch (e) { /* regenDocs가 토스트 처리 */ } finally { setBusy(false); }
  };

  // 직접 업로드(수정본·날인본·PDF교체)한 문서 = 현재 버전 kind가 'auto'가 아니고 실파일 보유 → 제외 재생성용
  const isUploaded = (k: string): boolean => {
    const vers = (p.docver && p.docver[k]) || [];
    const cur = vers[vers.length - 1];
    return !!(cur && cur.kind !== 'auto' && (cur.s3doc || cur.s3pdf));
  };
  const uploadedKeys = ALLKEYS.filter(isUploaded);
  const regenKeys = ALLKEYS.filter((k) => !isUploaded(k));

  const regenExcl = async () => {
    setBusy(true);
    try {
      await regenDocs(p, regenKeys, onPatch, toast);
      onPatch({ mhash: masterHash(p) });
    } catch (e) { /* regenDocs가 토스트 처리 */ } finally { setBusy(false); }
  };

  const changes = masterChanges(p.mhash, cur);

  return (
    <div className="alert" style={{ background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div>⚠ <b>공통정보(마스터·LP)가 변경되었습니다.</b> 뒤 단계 자동생성 문서가 이전 값 기준일 수 있어요.</div>
        {changes.length ? (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.7 }}>
            {changes.slice(0, 15).map((ch, i) => (
              <li key={i}><b>{ch.label}</b> · <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{ch.from}</span> → <b style={{ color: '#b45309' }}>{ch.to}</b></li>
            ))}
            {changes.length > 15 ? <li>…외 {changes.length - 15}건 변경</li> : null}
          </ul>
        ) : (
          <div style={{ fontSize: 12, marginTop: 4, color: '#92400e' }}>세부 항목은 확인되지 않지만 값이 바뀌었습니다 · 안전하게 재생성 권장</div>
        )}
        <div style={{ fontSize: 12, marginTop: 6, color: '#92400e' }}>실제 Word·PDF 13종을 한 번에 재생성할까요?</div>
      </div>
      <button className="btn pri sm" style={{ whiteSpace: 'nowrap' }} disabled={busy} onClick={regen}>
        {busy ? '생성 중… (약 1분)' : '🔄 전체 재생성 (13종)'}
      </button>
      {uploadedKeys.length > 0 && regenKeys.length > 0 && (
        <button className="btn sm" style={{ whiteSpace: 'nowrap', border: '1px solid #2563eb', color: '#2563eb', background: '#fff' }} disabled={busy}
          title={'직접 업로드·서명완료한 ' + uploadedKeys.length + '종은 그대로 두고 나머지 ' + regenKeys.length + '종만 재생성합니다'}
          onClick={regenExcl}>
          {busy ? '생성 중…' : '📄 업로드·서명완료 제외 재생성 (' + regenKeys.length + '종)'}
        </button>
      )}
      <button className="btn sm" style={{ whiteSpace: 'nowrap' }} disabled={busy} onClick={() => onPatch({ mhash: cur })}>나중에</button>
    </div>
  );
}
